'use client'

import {RefreshCw} from 'lucide-react'

import {Button} from '@/components/UiElements'
import type {historyLineItem, HistoryActionValue} from '@/types/order'
import {formatDateFromStringWithHour} from '@/utils/formatDate'

interface HistoryTimelineProps {
    title: string
    history: historyLineItem[]
    isLoading: boolean
    hasError: boolean
    onRefresh: () => void
    emptyMessage: string
    errorMessage: string
}

// The order service writes no "created" row for anything, so an unrecognised action
// type is a gap in its command_type map, not a creation event -- labelling it
// "Created" invented history that never happened.
const getHistoryActionLabel = (actionType: string) => {
    if (!actionType || actionType === 'unknown') return 'Updated'

    const normalizedAction = actionType.replaceAll('_', ' ').trim().toLowerCase()
    return normalizedAction.charAt(0).toUpperCase() + normalizedAction.slice(1)
}

// Rows written without an authenticated user carry a sentinel rather than a person.
// "unauthorized_source" is the pre-2026-09 spelling and still exists in old rows.
const SYSTEM_USER_NAMES = new Set(['system', 'unauthorized_source'])

const getHistoryUserLabel = (userName: string) => (
    !userName || SYSTEM_USER_NAMES.has(userName.trim().toLowerCase()) ? 'System' : userName
)

// action_value holds whatever the command changed: a status string, a quantity
// number, or a section object. Render the scalar forms, which are the ones an
// operator can act on; fall back to JSON for the rest so nothing is silently lost.
const formatActionValue = (value: HistoryActionValue['value']): string | null => {
    if (value === null || value === undefined) return null
    if (typeof value === 'string') {
        const trimmed = value.trim()
        if (!trimmed) return null
        return trimmed.replaceAll('_', ' ')
    }
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)

    // Section changes put a whole object here. Show enough to identify the change
    // without letting one row run away with the timeline.
    try {
        const serialized = JSON.stringify(value)
        if (!serialized || serialized === '{}') return null
        return serialized.length > 140 ? `${serialized.slice(0, 140)}…` : serialized
    } catch {
        return null
    }
}

const getHistoryDetail = (actionValue: historyLineItem['action_value']): string | null => {
    if (!actionValue) return null

    const current = formatActionValue(actionValue.value)
    if (current === null) return null

    const previous = formatActionValue(actionValue.previous ?? null)
    return previous === null ? current : `${previous} → ${current}`
}

const HistoryTimeline = ({
    title,
    history,
    isLoading,
    hasError,
    onRefresh,
    emptyMessage,
    errorMessage,
}: HistoryTimelineProps) => (
    <>
        <div className="mb-md flex items-center gap-md">
            <p className="text-title-md font-medium text-gray-900">{title}</p>
            <Button
                type="link"
                size="sm"
                className="!h-auto !px-0 !py-0 text-xs"
                icon={<RefreshCw className={isLoading ? 'animate-spin' : ''} size={14}/>}
                disabled={isLoading}
                onClick={onRefresh}
            >
                {isLoading ? 'Loading...' : 'Refresh'}
            </Button>
        </div>

        {hasError && history.length === 0 ? (
            <div className="flex min-h-[120px] flex-col items-center justify-center gap-md rounded-md bg-gray-50 p-lg text-center">
                <p className="text-sm text-gray-500">{errorMessage}</p>
                <Button type="alt" size="sm" onClick={onRefresh}>Retry</Button>
            </div>
        ) : history.length === 0 && !isLoading ? (
            <div className="flex min-h-[120px] items-center justify-center rounded-md bg-gray-50 p-lg text-center">
                <p className="text-sm text-gray-500">{emptyMessage}</p>
            </div>
        ) : history.length > 0 ? (
            <div className={`relative flex flex-col gap-lg transition-all ${isLoading ? 'opacity-50 grayscale' : ''}`}>
                {history.length > 1 && (
                    <span className="absolute bottom-[6px] left-[5px] top-[6px] w-px bg-gray-200"/>
                )}
                {history.map((item, index) => {
                    const detail = getHistoryDetail(item.action_value)

                    return (
                        <div key={item.id || `${item.created_at}-${index}`} className="relative pl-[28px]">
                            <span className="absolute left-0 top-[5px] z-[1] h-[11px] w-[11px] rounded-full bg-primary-500 ring-4 ring-white"/>
                            <div className="flex flex-wrap items-center gap-x-sm gap-y-xs">
                                <p className="text-sm font-medium text-gray-900">
                                    {getHistoryUserLabel(item.user_name)}
                                </p>
                                <p className="text-xs text-gray-400">
                                    {formatDateFromStringWithHour(item.created_at)}
                                </p>
                            </div>
                            <p className="mt-xs text-sm text-gray-600">
                                {getHistoryActionLabel(item.action_type)}
                                {detail && <span className="break-words text-gray-900"> — {detail}</span>}
                            </p>
                        </div>
                    )
                })}
            </div>
        ) : <div className="min-h-[120px]"/>}
    </>
)

export default HistoryTimeline
